import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Modal } from "react-bootstrap";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { loadCalendar, refreshCalendarCounts, loadRace, refreshRace, pickRecord, keepOurs, chooseSilk, setLoadError } from "../../redux/aiScraper/aiScraperSlice";
import DateField from "./DateField";
import { validateLoad, validateNewRace, validateRunner, validateResult, validateRecord } from "./aiScraperValidation";
import { FiDownloadCloud, FiExternalLink, FiCheckCircle } from "react-icons/fi";
import "../../Components/CSS/liveDataImporter.css";

// AI Scraper: one race at a time. Load reads ERA and our DB and shows what differs; every save is the admin's click
// and uses the same API as the manual admin forms. Nothing is kept between loads: "Keep ours" and a chosen record
// live in this page only, so a refresh shows the difference again.

const api = (path) => `${window.env.API_URL}${path}`;
// ERA writes typographic quotes ("Falconer’s", "O’Shea"); the admin APIs accept only the plain ones.
const plainQuotes = (value) => {
  if (typeof value === "string") return value.replace(/[‘’ʼ′]/g, "'").replace(/[“”″]/g, '"');
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, plainQuotes(v)]));
  }
  return value;
};
// One runner of the race: trials and old cards come without horse numbers, so the ERA id leads.
const runnerKey = (runner) => `${(runner.horse && runner.horse.era && runner.horse.era.eraId) || ""}#${runner.horseNo != null ? runner.horseNo : ""}`;

const CREATE_PATH = {
  owner: "/createowner", trainer: "/uploadtrainer", jockey: "/uploadJockey", horse: "/createhorse",
  RaceName: "/uploadRaceName", RaceKind: "/uploadRaceKind", RaceType: "/uploadRaceType",
};
// The code-check APIs the admin forms use; a damsire has no ERA id, so the DB numbers it.
const VERIFY_PATH = { owner: "/verifyownershortcode", trainer: "/verifytrainershortcode", jockey: "/verifyjockeyshortcode", horse: "/VerifyShortCode" };
const NEXT_CODE_PATH = { RaceName: "/getracenameshortcode", RaceKind: "/getracekindshortcode", RaceType: "/getracetypeshortcode" };
const LOOKUP_PATH = { RaceName: "/uploadRaceName", RaceKind: "/uploadRaceKind", RaceType: "/uploadRaceType" };
const PERSON_REQUIRED = {
  RaceName: ["NameEn", "NameAr"],
  RaceKind: ["NameEn", "NameAr"],
  RaceType: ["NameEn", "NameAr"],
  owner: ["NameEn", "NameAr"],
  trainer: ["NameEn", "NameAr", "DOB", "TrainerLicenseDate"],
  jockey: ["NameEn", "NameAr", "DOB", "JockeyLicenseDate"],
  horse: ["NameEn", "NameAr", "DOB", "Sex", "ColorID", "NationalityID", "ActiveOwner", "ActiveTrainer", "Breeder", "KindHorse"],
};
const HORSE_REF_FIELDS = {
  Trainer: "ActiveTrainer",
  Owner: "ActiveOwner",
  Sire: "Sire",
  Dam: "Dam",
  Damsire: "GSire",
};
// The runner's own reference(s) for a label (owner / trainer: the race's and the horse file's).
const refsOf = (runner, label) =>
  ({ Trainer: [runner.trainer, runner.fileTrainer], Owner: [runner.owner, runner.fileOwner], Sire: [runner.sire], Dam: [runner.dam], Damsire: [runner.damsire] }[label] || []).filter(Boolean);
// Same ERA record: by ERA id when both have one (two horses can share a name), else by name (a damsire has only a name).
const sameEraRecord = (a, b) => {
  if (!a || !b || !a.era || !b.era) return false;
  if (a.era.eraId && b.era.eraId) return String(a.era.eraId) === String(b.era.eraId);
  return String(a.era.name || "").trim().toUpperCase() === String(b.era.name || "").trim().toUpperCase();
};
const FIELD_LABEL = {
  shortCode: "Code", NameEn: "Name (English)", NameAr: "Name (Arabic)", DOB: "Date of birth", TrainerLicenseDate: "Licence date",
  JockeyLicenseDate: "Licence date", RegistrationDate: "Registration date",
};
const MISSING_LABEL = {
  ...FIELD_LABEL, Sex: "Sex (not in Saddle Battle)", ColorID: "Colour", NationalityID: "Bred in",
  ActiveOwner: "Owner (add it first)", ActiveTrainer: "Trainer (add it first)", Breeder: "Breeder", KindHorse: "Breed (not in Saddle Battle)",
};
const PRIZE_FIELDS = ["FirstPrice", "SecondPrice", "ThirdPrice", "FourthPrice", "FifthPrice", "SixthPrice"];

const errorText = (error) => {
  const message = error && error.response && error.response.data && error.response.data.message;
  if (Array.isArray(message)) return message.join(", ");
  return message || (error && error.message) || "Something went wrong";
};

const pickKey = (label, name) =>
  `${label}:${String(name || "")
    .toUpperCase()
    .replace(/\([A-Z]{2,4}\)/g, " ")
    .replace(/[.,'`’\-()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()}`;

// Pedigree lines of a runner and the horse-file field each one fills.
const PEDIGREE_KEY = { Sire: "Sire", Dam: "Dam", Damsire: "GSire" };
const PEDIGREE_KEY_OF = { Sire: true, Dam: true, GSire: true };

// A horse's page on ERA from its ERA id. ERA keeps the older ids (67358–77574) at /horses/h-<id> and the newer ones
// (2079296 and up) at /horses/<id>; each 404s as the other.
const eraHorseUrl = (id) => {
  const n = Number(id);
  // 999…/9000… are our own placeholder codes, not ERA ids: no page to open.
  if (!id || !Number.isInteger(n) || n <= 0 || /^(999|9000)/.test(String(n))) return null;
  return `https://emiratesracing.com/horses/${n < 1000000 ? "h-" : ""}${n}`;
};

const AiScraper = () => {
  const [date, setDate] = useState("");
  const [raceNo, setRaceNo] = useState("1");
  const dispatch = useDispatch();
  const { era, comparison: data, loading, error: loadError, keptOurs, silks: silkChoice, calendar } = useSelector((state) => state.aiScraper);
  const raceDays = useMemo(() => new Map(calendar.map((day) => [day.date, day])), [calendar]);
  // The courses in the calendar, for its legend.
  const legend = useMemo(() => {
    const yearAgo = dayjs().subtract(1, "year").format("YYYY-MM-DD");
    const seen = new Map(); // one entry per colour: our course and ERA's can be spelled differently
    calendar.filter((day) => !day.trial && day.color && day.date >= yearAgo).forEach((day) => seen.set(day.color, day.course));
    return [...seen.entries()].map(([color, course]) => [course, color]);
  }, [calendar]);

  useEffect(() => {
    dispatch(loadCalendar());
  }, [dispatch]);
  // Every Load and save can change what the day holds: recount races and results.
  useEffect(() => {
    if (data) dispatch(refreshCalendarCounts());
  }, [data, dispatch]);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false); // a second click arrives before the disabled button has rendered
  const [brokenSilks, setBrokenSilks] = useState({}); // silk images that did not load, by address
  const [creating, setCreating] = useState(null); // { kind, title, draft }
  const [reviewing, setReviewing] = useState(null); // horse number whose file is reviewed
  const [returnToReview, setReturnToReview] = useState(null); // horse number to go back to after adding a record
  const [editing, setEditing] = useState(null); // runner whose race entry is reviewed
  const [verifying, setVerifying] = useState(false); // result checked here before it is saved
  const [raceExtras, setRaceExtras] = useState({}); // new race: what ERA does not have, the admin's to check
  const [resultForm, setResultForm] = useState({ bestTurnOut: "", bestTurnPrice: 1000, videoLink: "" });

  // Saddle Battle keeps no trial races (the AI reads a horse's trials from its ERA page): a trial is only shown.
  const isTrial = Boolean(data) && data.source.stage === "TRIAL";
  const isPast = Boolean(date && dayjs(date).isBefore(dayjs(), "day"));
  const reviewRunner = reviewing != null && data ? data.runners.find((r) => runnerKey(r) === reviewing) : null;

  const closeCreate = () => {
    setCreating(null);
    if (returnToReview != null) setReviewing(returnToReview);
    setReturnToReview(null);
  };

  const load = async (raceNumber = raceNo) => {
    const problems = validateLoad(date, raceNumber);
    if (problems.length) {
      dispatch(setLoadError(problems.join(" ")));
      return;
    }
    if (busy.current) return;
    busy.current = true;
    const loaded = await dispatch(loadRace({ date, race: raceNumber }));
    busy.current = false;
    if (loadRace.fulfilled.match(loaded)) {
      const comparison = loaded.payload.comparison;
      const payload = comparison.header.payload;
      // A new race: every Add Race field, filled from ERA and ours to change.
      setRaceExtras({ ...payload, RaceStatus: "Due", BigRace: false });
      setResultForm((f) => ({
        ...f,
        bestTurnOut: comparison.results.bestTurnOut || "",
        bestTurnPrice: comparison.results.bestTurnPrice != null ? comparison.results.bestTurnPrice : 1000,
        videoLink: comparison.results.videoLink || "",
      }));
    }
  };

  // A name added to our lists after the Load: the field the admin left empty takes it.
  const headerPayload = data && data.header.payload;
  useEffect(() => {
    if (!headerPayload) return;
    setRaceExtras((form) => {
      const filled = { ...form };
      Object.keys(headerPayload).forEach((key) => {
        if ((filled[key] === "" || filled[key] == null) && headerPayload[key]) filled[key] = headerPayload[key];
      });
      return filled;
    });
  }, [headerPayload]);

  // After a save the DB is read again; ERA stays as loaded.
  const refresh = () => dispatch(refreshRace());

  const run = async (work, success) => {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    try {
      await work();
      await refresh();
      if (success) swal({ title: "Saved", text: success, icon: "success", timer: 1500, buttons: false });
    } catch (error) {
      swal({ title: "Not saved", text: errorText(error), icon: "error" });
      refresh();
    }
    busy.current = false;
    setSaving(false);
  };

  // A flow of several APIs: says which step stopped, as the earlier ones are already saved.
  const step = async (label, request) => {
    try {
      return await request();
    } catch (error) {
      throw new Error(`${label}: ${errorText(error)}`);
    }
  };

  /**
   * Puts a sire / dam / damsire / owner / trainer record on the horse files that point to it, so the admin does not
   * need "Review all" for it. Safe by design:
   * - the runner the admin acted on is always updated; other runners only when their field is still empty
   *   (a different value already on the file stays for "Review all");
   * - owner / trainer of a race that has run are never put on the horse file: that is the horse's owner today.
   * Returns a note for the admin when something was left for review.
   */
  const linkToHorseFiles = async (ref, recordId, runner) => {
    const fieldKey = HORSE_REF_FIELDS[ref && ref.label];
    if (!fieldKey || !recordId) return null;
    const isPerson = ref.label === "Owner" || ref.label === "Trainer";
    if (isPerson && data.race.hasRun) return null;
    const left = [];
    const done = new Set();
    for (const t of data?.runners || []) {
      const horseId = t.horse?.dbId;
      if (!horseId || done.has(horseId)) continue;
      const acted = Boolean(runner && runner.horse && runner.horse.dbId === horseId);
      if (!acted && !refsOf(t, ref.label).some((r) => sameEraRecord(r, ref))) continue;
      done.add(horseId);
      const current = t.horse.current ? t.horse.current[fieldKey] : null;
      if (current === recordId) continue;
      if (current && !acted) {
        left.push((t.horse.era && t.horse.era.name) || horseId);
        continue;
      }
      // Only this field. updatehorse also copies Sire to the horse's results, so the file's Sire goes with it.
      const body = { [fieldKey]: recordId };
      if (fieldKey !== "Sire" && t.horse.current?.Sire) body.Sire = t.horse.current.Sire;
      await step(`${ref.label} was saved, but ${(t.horse.era && t.horse.era.name) || "a horse"} was not updated`, () =>
        axios.put(api(`/updatehorse/${horseId}`), body)
      );
    }
    return left.length ? `${left.join(", ")} already ${left.length > 1 ? "have" : "has"} a different ${ref.label.toLowerCase()} — check it in "Review all".` : null;
  };

  const choose = async (ref, candidateId, candidate, runner) => {
    dispatch(pickRecord({ key: pickKey(ref.label, ref.era.name), id: candidateId, candidate }));
    if (!HORSE_REF_FIELDS[ref.label]) return refresh();
    let note = null;
    await run(async () => {
      note = await linkToHorseFiles(ref, candidateId, runner);
    }, null);
    if (note) swal({ title: "Left for review", text: note, icon: "info" });
    return null;
  };

  // ---- race header ---------------------------------------------------------
  const updateHeader = (field) => {
    const body = { totalPrize: era.race.totalPrize || undefined, Timing: data.header.payload.Timing, Endtiming: data.header.payload.Endtiming };
    if (field.patch) Object.assign(body, field.patch);
    else if (field.key === "totalPrize") body.PrizeNumber = data.header.payload.PrizeNumber;
    else if (field.key !== "Timing") body[field.key] = field.eraId;
    if (!body.totalPrize) {
      const dbPrize = data.header.fields.find((f) => f.key === "totalPrize");
      body.totalPrize = dbPrize ? dbPrize.db : undefined;
    }
    return run(() => axios.put(api(`/updaterace/${data.race.raceId}`), body), `${field.label} updated`);
  };

  // Race name, kind or type ERA uses and we lack: the admin form's fields (name, Arabic, short code), filled in,
  // the Arabic from the client's list when it has it.
  const addLookup = async (field) => {
    // The next short code, as the admin form proposes it: the highest so far plus one.
    let shortCode = "";
    try {
      const res = await axios.get(api(NEXT_CODE_PATH[field.key]));
      const rows = res.data.data || [];
      shortCode = String(rows.length ? Number(rows[0].maxshortCode) + 1 : 10);
    } catch (error) {
      shortCode = ""; // left to the DB, which numbers it
    }
    setCreating({
      kind: field.key,
      title: `New ${field.label.toLowerCase()}: ${field.era}`,
      draft: { NameEn: plainQuotes(field.era), NameAr: plainQuotes(field.eraAr || field.era), shortCode, arabicFromList: Boolean(field.eraAr) },
    });
  };

  // ---- records -------------------------------------------------------------
  const openCreate = async (ref, runner) => {
    if (ref.kind !== "horse") {
      setCreating({ kind: ref.kind, title: `New ${ref.label.toLowerCase()}: ${ref.era.name}`, draft: { ...ref.draft }, ref, runner });
      return;
    }
    if (ref.draft) {
      setCreating({ kind: "horse", title: `New ${ref.label.toLowerCase()}: ${ref.era.name}`, draft: { ...ref.draft }, ref, runner });
      return;
    }
    // Sire or dam: its own ERA page has the details.
    setSaving(true);
    try {
      const res = await axios.get(api(`/aiscraper/horse/${ref.era.eraId}`), { params: { date: era.race.date, kind: data.header.payload.HorseKindinRace, role: ref.label } });
      setCreating({ kind: "horse", title: `New ${ref.label.toLowerCase()}: ${ref.era.name}`, draft: res.data.draft, ref, runner });
    } catch (error) {
      swal({ title: "ERA", text: errorText(error), icon: "error" });
      closeCreate();
    }
    setSaving(false);
  };

  const saveCreate = async (values) => {
    let saved = false;
    let note = null;
    await run(async () => {
      const { silkUrl, breederOnEra, countryOnEra, arabicFromList, ...body } = values; // eslint-disable-line no-unused-vars
      if (body.shortCode === "") delete body.shortCode;
      if (body.shortCode && VERIFY_PATH[creating.kind]) {
        const taken = await axios.post(api(VERIFY_PATH[creating.kind]), { shortCode: body.shortCode });
        if (taken.data.data) throw new Error(`Code ${body.shortCode} already belongs to ${String(taken.data.data.NameEn).trim()}. Enter another code.`);
      }
      const res = await axios.post(api(CREATE_PATH[creating.kind]), plainQuotes(body));
      if (creating.kind === "owner" && silkUrl && values.useSilk !== false) {
        await step("The owner was added, but the silk was not copied", () => axios.post(api(`/aiscraper/owner-silk/${res.data.data._id}`), { url: silkUrl }));
      }
      saved = true;
      if (creating.ref) note = await linkToHorseFiles(creating.ref, res.data?.data?._id, creating.runner);
    }, "Added to Saddle Battle");
    // Only now: the popup behind it must open on the DB as it is after this save.
    if (saved) closeCreate();
    if (note) swal({ title: "Left for review", text: note, icon: "info" });
  };

  const sameHorse = (runner, candidate) =>
    swal({
      title: `Is ${candidate.name} (code ${candidate.shortCode}) the same horse?`,
      text: `Its code becomes the ERA id ${runner.horse.era.eraId}, so this question does not come back.`,
      buttons: ["Cancel", "Yes, same horse"],
    }).then((yes) => {
      if (!yes) return null;
      return run(() => axios.put(api(`/updatehorse/${candidate._id}`), { shortCode: runner.horse.era.eraId, ...(candidate.Sire ? { Sire: candidate.Sire } : {}) }), "Horse linked to ERA");
    });

  const saveHorseFile = (runner, chosen) => {
    const fromEra = runner.horse.diffs.filter((d) => chosen[d.key] === "era" && d.patch);
    // A field whose ERA record is not in Saddle Battle yet is not "kept": it stays in Review until it is added.
    const kept = runner.horse.diffs.filter((d) => chosen[d.key] !== "era" && d.patch);
    dispatch(keepOurs(kept.map((d) => `${runner.horse.dbId}:${d.key}`)));
    setReviewing(null);
    if (!fromEra.length) return null;
    const body = Object.assign({ Sire: runner.horse.current.Sire }, ...fromEra.map((d) => d.patch));
    return run(() => axios.put(api(`/updatehorse/${runner.horse.dbId}`), body), "Horse file updated");
  };

  // ---- runners -------------------------------------------------------------
  const entryBody = (runner) => ({ ...runner.entry.body, CapColor: silkChoice[runnerKey(runner)] || runner.entry.body.CapColor });

  const addRunner = (runner) => {
    const problems = validateRunner(runner);
    if (problems.length) return swal({ title: "Cannot add yet", text: problems.join("\n"), icon: "warning" });
    return run(() => axios.post(api(`/addracehorses/${data.race.raceId}`), { HorseEntry: [entryBody(runner)] }), `${runner.horse.era.name} added to the race`);
  };

  const openEntryDiffs = (runner) => runner.entry.diffs.filter((d) => !keptOurs[`entry:${runner.entry.rowId}:${d.key}`]);

  const saveEntry = (runner, chosen) => {
    const fromEra = runner.entry.diffs.filter((d) => chosen[d.key] === "era");
    const notAdded = (d) => !runner.nonRunner && d.key === "JockeyOnRace" && !runner.jockey.dbId;
    dispatch(keepOurs(openEntryDiffs(runner).filter((d) => chosen[d.key] !== "era" && !notAdded(d)).map((d) => `entry:${runner.entry.rowId}:${d.key}`)));
    setEditing(null);
    if (!fromEra.length) return null;
    const body = { Rowid: runner.entry.rowId, HorseModelId: runner.horse.dbId };
    fromEra.forEach((d) => {
      if (d.key === "HorseRunningStatus") body.HorseRunningStatus = String(runner.entry.body.HorseRunningStatus);
      else body[d.key] = entryBody(runner)[d.key];
    });
    const path = data.race.hasRun ? `/aiscraper/race-entry/${runner.entry.rowId}` : `/EditRaceHorses/${data.race.raceId}`;
    return run(() => axios.put(api(path), body), "Runner updated");
  };

  // A difference ERA's side of which is not in our records: add it from the popup, then come back to it.
  const refOfDiff = (runner, key) =>
    ({ ActiveOwner: runner.fileOwner || runner.owner, ActiveTrainer: runner.fileTrainer || runner.trainer, Sire: runner.sire, Dam: runner.dam, GSire: runner.damsire, JockeyOnRace: runner.jockey }[key]);

  const addFromReview = (runner, key) => {
    if (reviewing != null) setReturnToReview(reviewing);
    setReviewing(null);
    setEditing(null);
    openCreate(refOfDiff(runner, key), runner);
  };

  const recordActions = (runner) => (d) => {
    const ref = refOfDiff(runner, d.key);
    if (!ref || !ref.era || !ref.era.name) return null;
    const cleanEra = String(ref.era.name).replace(/\s*\([A-Z]{2,4}\)$/, "").trim().toLowerCase();
    const hasExact = (ref.candidates || []).some((c) => String(c.name).trim().toLowerCase() === cleanEra);
    return (
      <div className="imp-review-add">
        {pickButtons(ref, runner)}
        {!hasExact && (
          <button className="imp-btn imp-btn--primary imp-btn--fix" disabled={saving} onClick={() => addFromReview(runner, d.key)}>
            + Add {String(ref.era.name).replace(/\s*\([A-Z]{2,4}\)$/, "")}
          </button>
        )}
      </div>
    );
  };

  // One record to pick: "Use NAME #code", its ERA page, and the facts for and against it.
  const pickButton = (ref, runner, c) => {
    const href = ref.kind === "horse" ? eraHorseUrl(c.shortCode) : null;
    return (
      <span key={c._id} className="imp-pick-wrap">
        <span className="imp-pick">
          <button className="imp-pick-use" disabled={saving} onClick={() => choose(ref, c._id, c, runner)}>
            Use {c.name} <span className="imp-pick-code">#{c.shortCode}</span>
          </button>
          {href && (
            <a className="imp-pick-era" href={href} target="_blank" rel="noreferrer" title={`Open ${c.name} on ERA`}>
              ERA <FiExternalLink size={10} />
            </a>
          )}
        </span>
        {(c.notes || []).map((n) => (
          <span key={n.text} className={`imp-fact ${n.good ? "imp-fact--good" : "imp-fact--bad"}`}>{n.good ? "✓" : "✗"} {n.text}</span>
        ))}
      </span>
    );
  };
  // Records the facts ruled out are not shown: a record that is not the horse is no choice for the admin.
  const pickButtons = (ref, runner) => (ref.candidates || []).map((c) => pickButton(ref, runner, c));

  const removeRunner = (extra) =>
    swal({
      title: `Remove ${extra.horseName} from this race?`,
      text: "It is on the Saddle Battle race card but not on ERA.",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    }).then((yes) => (yes ? run(() => axios.delete(api(`/DeleteRaceHorse/${extra.rowId}`)), "Runner removed") : null));

  // ---- final step ----------------------------------------------------------
  // The race takes owner and trainer from the horse file, not from what ERA shows.
  const oldConnections = () =>
    data.runners
      .map((r) => {
        const old = (r.horse.diffs || []).filter((d) => d.key === "ActiveOwner" || d.key === "ActiveTrainer");
        return old.length ? `${r.horse.era.name}: ${old.map((d) => `${d.label} ${d.db}`).join(", ")}` : null;
      })
      .filter(Boolean);

  // The form's time is UAE time wherever the admin sits: a browser in Pakistan must not move it an hour.
  const raceBody = () => {
    const timing = new Date(`${raceExtras.Day}T${raceExtras.StartTime}:00+04:00`);
    return {
      ...data.header.payload,
      ...raceExtras,
      Timing: timing.toISOString(),
      Endtiming: new Date(timing.getTime() + 1000).toISOString(),
    };
  };

  const addRace = () => {
    const runners = data.runners.length;
    return swal({
      title: runners ? `Add race ${raceExtras.RaceNumber} with ${runners} runners?` : `Add race ${raceExtras.RaceNumber} without horses?`,
      text: runners
        ? `${oldConnections().length ? `These run under the connections on the horse file, not ERA's: ${oldConnections().join("; ")}. ` : ""}${!isPast ? "People tracking these horses get an email and a notification, as when a race is added by hand." : ""}`.trim()
        : "ERA has not declared the horses yet. Load this race again after the declarations to add them and publish it.",
      buttons: ["Cancel", "Add race"],
    }).then((yes) => {
      if (!yes) return null;
      return run(async () => {
        const created = await step("The race was not created", () => axios.post(api("/createrace"), raceBody()));
        if (!runners) return;
        const raceId = created.data.data._id;
        await step("The race was created, but its runners were not added", () => axios.post(api(`/addracehorses/${raceId}`), { HorseEntry: data.runners.map(entryBody) }));
        await step("The race and runners were added, but it was not published", () => axios.put(api(`/publishrace/${raceId}`)));
      }, "Race added");
    });
  };

  const publishRace = () => run(() => axios.put(api(`/publishrace/${data.race.raceId}`)), "Race published");

  const resultEntries = () =>
    data.results.rows.map((row) => ({
      ...row.entry,
      BestTurnOut: resultForm.bestTurnOut,
      BestTurnPrice: Number(resultForm.bestTurnPrice) || 0,
      VideoLink: resultForm.videoLink,
    }));

  // Same split as the result API: a place's prize is shared when horses dead-heat for it.
  const prizeOf = (row) => {
    const field = data.header.fields.find((f) => f.key === PRIZE_FIELDS[row.rank - 1]);
    if (!field || !field.db) return 0;
    return field.db / data.results.rows.filter((other) => other.rank === row.rank).length;
  };

  const saveResult = () => {
    const update = data.results.state === "DIFF";
    setVerifying(false);
    return run(
      () =>
        update
          ? axios.put(api(`/EditRaceResultV3/${data.race.raceId}`), { ResultEntry: resultEntries() })
          : axios.post(api(`/ResultCreationV2/${data.race.raceId}`), { ResultEntry: resultEntries() }),
      update ? "Result updated" : "Result added"
    );
  };

  // ---- rendering -----------------------------------------------------------
  const chip = (status) => {
    if (status === "MATCHED") return <span className="imp-chip imp-chip--matched">Matched</span>;
    if (status === "CHANGED") return <span className="imp-chip imp-chip--changed">Changed</span>;
    if (status === "SUGGESTED") return <span className="imp-chip imp-chip--suggested">Which one?</span>;
    return <span className="imp-chip imp-chip--new">New</span>;
  };

  const refLine = (ref, runner) => {
    if (!ref || !ref.era || !ref.era.name) return null;
    if (isTrial) {
      return (
        <li key={ref.label} className="is-muted">
          <span className="imp-check-label">{ref.label}</span>
          <span>{ref.era.name}</span>
        </li>
      );
    }
    const open = ref.status !== "MATCHED";
    // Already a question in "Review all": answered there only, so the row does not ask it twice.
    // A horse on file with differences comes as CHANGED; a NEW or SUGGESTED one has no "Review all" yet.
    const inReview = Boolean(runner && runner.horse && ["MATCHED", "CHANGED"].includes(runner.horse.status)
      && openDiffs(runner).some((d) => d.key === PEDIGREE_KEY[ref.label]));
    if (open && inReview) {
      return (
        <li key={ref.label} className="is-bad">
          <span className="imp-check-mark">!</span>
          <span className="imp-check-label">{ref.label}</span>
          <span>
            {ref.era.name}{" "}
            <button className="imp-link-btn" onClick={() => setReviewing(runnerKey(runner))}>fix in Review all</button>
          </span>
        </li>
      );
    }
    return (
      <li key={ref.label} className={open ? "is-bad" : "is-ok"}>
        <span className="imp-check-mark">{open ? "!" : "✓"}</span>
        <span className="imp-check-label">{ref.label}</span>
        {!open ? (
          <span>{ref.era.name}{ref.dbName && ref.dbName.toUpperCase() !== String(ref.era.name).toUpperCase() ? <span className="imp-hint"> (System: {ref.dbName})</span> : null}</span>
        ) : (
          <div className="imp-ref-open">
            <div><span className="imp-ref-source">ERA</span> {ref.era.name}</div>
            <div>
              <span className="imp-ref-source">System</span>{" "}
              {ref.status === "NEW"
                ? "Not found"
                : ref.candidates.length > 1
                  ? `${ref.candidates.length} similar records. Which one is it?`
                  : "1 similar record. Is it the same?"}
            </div>
            <div className="imp-ref-actions">
              {ref.status === "SUGGESTED" && pickButtons(ref, runner)}
              <button className={`imp-btn imp-btn--fix ${ref.status === "NEW" ? "imp-btn--primary" : ""}`} disabled={saving} onClick={() => openCreate(ref, runner)}>+ Add as new</button>
            </div>
          </div>
        )}
      </li>
    );
  };

  const shownSilk = (runner) => (runner.silks.find((s) => s._id === silkChoice[runnerKey(runner)]) || {}).url || runner.silkDb;

  const openDiffs = (runner) => (runner.horse.diffs || []).filter((d) => !keptOurs[`${runner.horse.dbId}:${d.key}`]);

  // A new horse's file takes its owner, trainer, sire and dam as they stand: these come first, so it is not saved
  // with an empty owner or an Unknown dam.
  const firstFix = (runner) =>
    [runner.owner, runner.trainer, runner.sire, runner.dam]
      .filter((ref) => ref && ref.era && ref.era.name && (ref.status === "NEW" || ref.status === "SUGGESTED"))
      .map((ref) => ref.label);
  const addHorseNote = (pending) =>
    pending.length > 0 && <span className="imp-hint imp-warn-text">First fix: {pending.join(", ")}</span>;

  const renderHorseState = (runner) => {
    if (isTrial) return null;
    const h = runner.horse;
    const pending = firstFix(runner);
    if (h.status === "NEW") {
      return (
        <div className="imp-runner-state">
          {chip("NEW")}
          <button className="imp-btn imp-btn--primary imp-btn--fix" disabled={saving || pending.length > 0} onClick={() => openCreate(h)}>+ Add horse</button>
          {addHorseNote(pending)}
        </div>
      );
    }
    if (h.status === "SUGGESTED") {
      return (
        <div className="imp-runner-state">
          {chip("SUGGESTED")}
          {h.candidates.map((c) => (
            <button key={c._id} className="imp-btn imp-btn--fix" disabled={saving} onClick={() => sameHorse(runner, c)}>
              Same as {c.name} #{c.shortCode}
            </button>
          ))}
          <button className="imp-btn imp-btn--fix" disabled={saving || pending.length > 0} onClick={() => openCreate(h)}>+ Different horse</button>
          {addHorseNote(pending)}
        </div>
      );
    }
    const diffs = openDiffs(runner);
    if (!diffs.length) return <div className="imp-runner-state">{(h.diffs || []).length ? <span className="imp-chip imp-chip--matched">Kept current</span> : chip("MATCHED")}</div>;
    return (
      <div className="imp-runner-state">
        {chip("CHANGED")}
        <button className="imp-btn imp-btn--review imp-btn--fix" disabled={saving} title="Everything to fix on this horse's file, in one popup" onClick={() => setReviewing(runnerKey(runner))}>Review all {diffs.length}</button>
      </div>
    );
  };

  const renderEntryState = (runner) => {
    if (isTrial) return <span className="imp-muted">Trial</span>;
    const entry = runner.entry;
    if (!data.race.exists) return <span className="imp-muted">with the race</span>;
    if (entry.state === "NEW") {
      const blocked = runner.horse.status === "NEW" || runner.horse.status === "SUGGESTED" || (!runner.nonRunner && runner.jockey.status !== "MATCHED");
      return <button className="imp-btn imp-btn--primary imp-btn--fix" disabled={saving || blocked} onClick={() => addRunner(runner)}>Add to race</button>;
    }
    if (entry.state === "DIFF") {
      const open = openEntryDiffs(runner);
      if (!open.length) return <span className="imp-chip imp-chip--matched">Kept current</span>;
      return <button className="imp-btn imp-btn--primary imp-btn--fix" disabled={saving} onClick={() => setEditing(runner)}>Review {open.length}</button>;
    }
    return <span className="imp-chip imp-chip--matched">Same</span>;
  };

  // What of this race is already in Saddle Battle, at a glance: race card, runners, records, result, AI.
  // The dot on a race tab: red while the race or its horses are not added, yellow while only the result is missing
  // (a day gone by), none when everything is in. Counts come with the calendar.
  const tabDot = (n) => {
    const day = raceDays.get(date);
    if (!day || !day.races) return null;
    const no = Number(n);
    if (!day.races.includes(no) || !(day.withHorses || []).includes(no)) return "red";
    if (dayjs(date).isAfter(dayjs(), "day")) return null;
    return day.withResult.includes(no) ? null : "yellow";
  };
  const TAB_DOT_TITLE = { red: "Race or horses not added yet", yellow: "Result not added yet" };

  const renderStatus = () => {
    if (isTrial) return null;
    const s = data.summary;
    const race = data.race;
    const total = data.runners.length;
    const added = data.runners.filter((r) => r.entry && r.entry.state !== "NEW").length;
    const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
    const items = [];
    items.push(!race.exists
      ? { label: "Race card", text: "Not added", tone: "bad" }
      : { label: "Race card", text: `${race.published ? "Added" : "Added, not published"}${s.headerOpen ? ` · ${plural(s.headerOpen, "field")} differ` : ""}`, tone: race.published && !s.headerOpen ? "good" : "warn" });
    // No declarations yet: nothing to add or check, which is neither good nor bad.
    const declared = data.source.stage !== "PROGRAMME";
    items.push(!declared
      ? { label: "Horses in race", text: "Not declared on ERA yet", tone: "muted" }
      : !race.exists
        ? { label: "Horses in race", text: `0 of ${total} added`, tone: "bad" }
        : { label: "Horses in race", text: `${added} of ${total} added${s.entriesOpen ? ` · ${s.entriesOpen} to review` : ""}${s.extraOpen ? ` · ${s.extraOpen} not on ERA` : ""}`, tone: added === total && !s.entriesOpen && !s.extraOpen ? "good" : added ? "warn" : "bad" });
    items.push(!declared
      ? { label: "Horse, owner, trainer, jockey records", text: "No horses yet", tone: "muted" }
      : { label: "Horse, owner, trainer, jockey records", text: s.recordsOpen ? `${s.recordsOpen} to review` : "All match", tone: s.recordsOpen ? "warn" : "good" });
    const result = data.results.state;
    items.push(result === "NONE"
      ? { label: "Result", text: "ERA has no result yet", tone: "muted" }
      : result === "NEW"
        ? { label: "Result", text: "Not added", tone: "bad" }
        : result === "DIFF"
          ? { label: "Result", text: "Added, differs from ERA", tone: "warn" }
          : { label: "Result", text: "Added", tone: "good" });
    items.push({ label: "AI analysis", text: race.aiRanAt ? `Ran ${dayjs(race.aiRanAt).format("DD MMM YYYY, HH:mm")}` : "Not run", tone: race.aiRanAt ? "good" : "muted" });
    return (
      <div className="imp-status">
        {items.map((item) => (
          <div key={item.label} className={`imp-status-item imp-status-item--${item.tone}`}>
            <span className="imp-status-label">{item.label}</span>
            <span className="imp-status-text">{item.text}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderBanner = () => {
    const s = data.summary;
    if (s.allMatch) {
      return <div className="imp-verdict imp-verdict--good"><FiCheckCircle /> Everything matches ERA. No action needed.</div>;
    }
    if (isTrial) {
      return <div className="imp-verdict imp-verdict--neutral">This is a trial. Saddle Battle does not keep trials as races, so there is nothing to add here. The AI reads a horse&apos;s trials from its ERA page.</div>;
    }
    if (data.source.stage === "PROGRAMME") {
      return <div className="imp-verdict imp-verdict--neutral">Horse declaration not added yet on ERA. The race details can be added now; load it again after the declarations for the horses.</div>;
    }
    if (data.race.exists && !data.race.hasRun && data.race.aiRanAt && (s.headerOpen || s.entriesOpen || s.extraOpen)) {
      return <div className="imp-verdict imp-verdict--neutral">AI ran on this race before these changes. It does not run again by itself.</div>;
    }
    return null;
  };

  // ---- new race: the Add Race form, filled from ERA --------------------------
  const setField = (key, value) => setRaceExtras((x) => ({ ...x, [key]: value }));
  const eraOf = (key) => data.header.fields.find((f) => f.key === key);

  const textField = (key, label, props = {}) => (
    <label className="imp-field" key={key}>
      <span className="imp-field-label">{label} <span className="imp-req">*</span></span>
      <input value={raceExtras[key] != null ? raceExtras[key] : ""} onChange={(e) => setField(key, e.target.value)} {...props} />
    </label>
  );

  const selectField = (key, label, options, { lookup = key, required = true } = {}) => {
    const era = eraOf(lookup);
    // ERA's name is not in our list: it can be added from here, as on the old screen.
    const missing = era && era.era && !era.eraId && !raceExtras[key];
    return (
      <label className="imp-field" key={key}>
        <span className="imp-field-label">{label} {required && <span className="imp-req">*</span>}</span>
        <select value={raceExtras[key] || ""} onChange={(e) => setField(key, e.target.value)}>
          <option value="">Select…</option>
          {options.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
        </select>
        {missing && (
          <span className="imp-hint imp-warn-text">
            ERA: {String(era.era)} is not in Saddle Battle{" "}
            {LOOKUP_PATH[lookup] && <button type="button" className="imp-btn imp-btn--fix" disabled={saving} onClick={() => addLookup(era)}>+ Add</button>}
          </span>
        )}
      </label>
    );
  };

  const renderRaceForm = () => {
    // Every list defaults to empty: a backend a version behind must not take the page down with it.
    const lists = data.options.race;
    const o = Object.fromEntries(
      ["raceNames", "raceKinds", "raceTypes", "horseKinds", "meetingTypes", "courses", "goings", "currencies", "trackLengths", "descriptions"]
        .map((key) => [key, Array.isArray(lists[key]) ? lists[key] : []])
    );
    const lengths = o.trackLengths.filter((t) => t.course === raceExtras.RaceCourse);
    return (
      <div className="imp-race">
        <div className="imp-race-head">
          <strong>1. Add race</strong>
          <span className="imp-hint">Filled from ERA · {data.raceInfo.conditions}</span>
        </div>
        <div className="imp-race-form imp-race-form--full">
          <label className="imp-field" key="Day">
            <span className="imp-field-label">Race date <span className="imp-req">*</span></span>
            <DatePicker
              format={["DD/MM/YYYY", "DD-MM-YYYY", "DD MM YYYY"]}
              value={raceExtras.Day ? dayjs(raceExtras.Day) : null}
              onChange={(picked) => setField("Day", picked ? picked.format("YYYY-MM-DD") : "")}
              placeholder="DD/MM/YYYY"
              allowClear={false}
            />
          </label>
          {textField("StartTime", "Start time (UAE)", { type: "time" })}
          {textField("RaceNumber", "Race number", { type: "number", min: 1 })}
          {textField("MeetingCode", "Meeting code")}
          {selectField("HorseKindinRace", "Horse kind", o.horseKinds)}
          {selectField("RaceName", "Race name", o.raceNames)}
          {selectField("Sponsor", "Sponsor", data.options.sponsors)}
          {selectField("RaceKind", "Race kind", o.raceKinds)}
          {selectField("RaceType", "Race type", o.raceTypes)}
          {o.descriptions.length > 0 && (
            <label className="imp-field imp-field--wide">
              <span className="imp-field-label">Description from the list ({o.descriptions.length})</span>
              <select
                value={o.descriptions.findIndex((d) => d.en === raceExtras.DescriptionEn)}
                onChange={(e) => {
                  const picked = o.descriptions[Number(e.target.value)];
                  if (picked) setRaceExtras((x) => ({ ...x, DescriptionEn: picked.en, DescriptionAr: picked.ar }));
                }}
              >
                <option value={-1}>Choose one to fill English and Arabic…</option>
                {o.descriptions.map((d, i) => (
                  <option key={i} value={i}>{d.band ? `${d.band} · ` : ""}{d.en.slice(0, 140)}</option>
                ))}
              </select>
            </label>
          )}
          {textField("DescriptionEn", "Description (English)")}
          {textField("DescriptionAr", "Description (Arabic)", { dir: "rtl" })}
          {selectField("MeetingType", "Meeting type", o.meetingTypes)}
          {textField("WeatherDegree", "Weather degree", { type: "number" })}
          <label className="imp-field">
            <span className="imp-field-label">Weather type</span>
            <select value={raceExtras.WeatherType || "Sunny"} onChange={(e) => setField("WeatherType", e.target.value)}>
              <option value="Sunny">Sunny</option>
              <option value="Cloudy">Cloudy</option>
            </select>
          </label>
          <label className="imp-field">
            <span className="imp-field-label">Race course <span className="imp-req">*</span></span>
            <select value={raceExtras.RaceCourse || ""} onChange={(e) => setRaceExtras((x) => ({ ...x, RaceCourse: e.target.value, TrackLength: "" }))}>
              <option value="">Select…</option>
              {o.courses.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </label>
          {selectField("TrackLength", "Track length", lengths)}
          <label className="imp-field">
            <span className="imp-field-label">Race status</span>
            <select value={raceExtras.RaceStatus || "Due"} onChange={(e) => setField("RaceStatus", e.target.value)}>
              <option value="Due">Due</option>
              <option value="Live">Live</option>
            </select>
          </label>
          {selectField("TrackCondition", "Track condition", o.goings)}
          {selectField("Currency", "Currency", o.currencies)}
          <label className="imp-field">
            <span className="imp-field-label">Big race</span>
            <select value={raceExtras.BigRace ? "yes" : "no"} onChange={(e) => setField("BigRace", e.target.value === "yes")}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          {textField("totalPrize", "Total prize", { type: "number", min: 1 })}
          <label className="imp-field">
            <span className="imp-field-label">Prize places</span>
            <select value={raceExtras.PrizeNumber || 5} onChange={(e) => setField("PrizeNumber", Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={6}>6 (six positions)</option>
            </select>
          </label>
        </div>
      </div>
    );
  };

  const renderHeader = () => (!data.race.exists && !isTrial && data.options.race ? renderRaceForm() : (
    <div className="imp-race">
      <div className="imp-race-head">
        <strong>1. Race details</strong>
        <span className="imp-hint">{data.raceInfo.course} · {data.raceInfo.date} · Race {data.raceInfo.raceNo} · {data.raceInfo.conditions}</span>
      </div>
      <table className="imp-table imp-table--compact">
        <thead><tr><th>Field</th><th>ERA</th><th>System</th><th></th></tr></thead>
        <tbody>
          {data.header.fields.map((f) => (
            <tr key={f.key} className={f.same ? "" : "imp-row--open"}>
              <td>{f.label}</td>
              <td>{f.era != null ? String(f.era) : <span className="imp-muted">not on ERA</span>}</td>
              <td>{data.race.exists ? (f.db != null ? String(f.db) : <span className="imp-muted">empty</span>) : <span className="imp-muted">race not in Saddle Battle</span>}</td>
              <td className="imp-cell-action">
                {isTrial ? null : !f.eraId && LOOKUP_PATH[f.key] && f.era ? (
                  <button className="imp-btn imp-btn--fix" disabled={saving} onClick={() => addLookup(f)}>+ Add</button>
                ) : data.race.exists && !f.same ? (
                  keptOurs[`header:${f.key}`] ? (
                    <span className="imp-hint">Kept current</span>
                  ) : (
                    <span className="imp-actions">
                      <button className="imp-btn imp-btn--primary imp-btn--fix" disabled={saving || (!f.eraId && !f.patch && f.key !== "Timing" && f.key !== "totalPrize")} onClick={() => updateHeader(f)}>Use ERA</button>
                      <button className="imp-btn imp-btn--fix" onClick={() => dispatch(keepOurs([`header:${f.key}`]))}>Keep current</button>
                    </span>
                  )
                ) : f.same ? <span className="imp-chip imp-chip--matched">Same</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ));

  const renderRunners = () => (
    <div className="imp-race">
      <div className="imp-race-head">
        <strong>2. Runners ({data.runners.length})</strong>
        <span className="imp-hint">{data.source.runnerSource === "entries" ? "Entries: the draw is not out yet" : ""}</span>
      </div>
      <div className="imp-table-wrap">
        <table className="imp-table imp-runner-table">
          <thead><tr><th>No. / Gate</th><th>Horse</th><th>Connections &amp; Pedigree</th><th>Silk</th><th>Race Entry</th></tr></thead>
          <tbody>
            {data.runners.map((r) => (
              <tr key={runnerKey(r)} className={r.nonRunner ? "imp-row--scratched" : ""}>
                <td>
                  <div className="imp-runner-num">{r.horseNo ?? r.entry?.body?.HorseNo ?? "NR"}</div>
                  <div className="imp-runner-gate">{r.nonRunner ? "Non-runner" : r.gate != null && Number(r.gate) > 0 ? `gate ${r.gate}` : "no draw"}</div>
                </td>
                <td>
                  <a href={`https://emiratesracing.com/${r.horse.era.path}`} target="_blank" rel="noreferrer">{r.horse.era.name} <FiExternalLink size={11} /></a>
                  <div className="imp-hint">{r.age} YO · {r.sex} · {r.colour} · {r.country}</div>
                  <div className="imp-hint">{r.weight} kg · rating {r.rating != null ? r.rating : "NOR"}{r.jockeyClaim ? ` · claim ${r.jockeyClaim}` : ""}</div>
                  <div className="imp-hint">{(r.equipment || []).join(", ") || "No equipment"}</div>
                  {r.ownerOnRace && <div className="imp-hint">Runs for: {r.ownerOnRace} · {r.trainerOnRace}</div>}
                  {renderHorseState(r)}
                  {r.profileError && <div className="imp-hint imp-warn-text">Horse page did not load: pedigree not checked</div>}
                </td>
                <td className="imp-small">
                  <ul className="imp-checks">
                    {refLine(r.owner, r)}
                    {refLine(r.trainer, r)}
                    {refLine(r.jockey, r)}
                    {refLine(r.sire, r)}
                    {refLine(r.dam, r)}
                    {refLine(r.damsire, r)}
                  </ul>
                </td>
                <td>
                  <div className="imp-silk-cell">
                    {r.silkEra && <img className="imp-silk-thumb" src={r.silkEra} alt="" title="ERA" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                    {r.silkDb && !brokenSilks[shownSilk(r)]
                      ? <img className="imp-silk-thumb" src={shownSilk(r)} alt="" title="System" onError={() => setBrokenSilks((b) => ({ ...b, [shownSilk(r)]: true }))} />
                      : <span className="imp-hint">{r.silkDb ? "Silk image is missing" : "No silk in Saddle Battle"}</span>}
                    {r.silks.length > 1 && r.entry.state !== "MATCH" && (
                      <select
                        value={silkChoice[runnerKey(r)] || r.entry.body.CapColor || ""}
                        onChange={(e) => dispatch(chooseSilk({ horseNo: runnerKey(r), id: e.target.value }))}
                      >
                        {r.silks.map((s, i) => <option key={s._id} value={s._id}>Silk {i + 1}{s.isDefault ? " (default)" : ""}</option>)}
                      </select>
                    )}
                    {(!r.silkDb || brokenSilks[shownSilk(r)]) && r.owner.dbId && r.silkEra && (
                      <button
                        className="imp-btn imp-btn--fix"
                        disabled={saving}
                        onClick={() => run(() => axios.post(api(`/aiscraper/owner-silk/${r.owner.dbId}`), { url: r.silkEra, replaces: r.silkDb ? (r.silks.find((s) => s.url === shownSilk(r)) || {})._id : undefined }), "Silk copied from ERA")}
                      >
                        {r.silkDb ? "Replace from ERA" : "Copy"}
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  {renderEntryState(r)}
                  {r.entry.equipmentMissing && (
                    <div className="imp-hint imp-warn-text">
                      {(r.equipment || []).join(", ")}: not in Saddle Battle equipment{" "}
                      <button
                        className="imp-link"
                        disabled={saving}
                        onClick={() => {
                          const code = r.equipmentCode;
                          return run(() => axios.post(api("/uploadEquipment"), { NameEn: code, NameAr: code }), "Equipment added");
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.extraRunners.length > 0 && (
        <table className="imp-table imp-table--compact">
          <thead><tr><th colSpan={2}>On System race card, not on ERA</th></tr></thead>
          <tbody>
            {data.extraRunners.map((x) => (
              <tr key={x.rowId}>
                <td>{x.horseNo}. {x.horseName}{x.running ? "" : " (non-runner)"}</td>
                <td className="imp-cell-action">
                  <button className="imp-btn imp-btn--fix" disabled={saving} onClick={() => removeRunner(x)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const blockers = () => {
    const open = [];
    data.runners.forEach((r) => {
      [r.horse, r.owner, r.trainer, r.jockey, r.sire, r.dam, r.damsire].forEach((ref) => {
        if (ref && ref.era && ref.era.name && (ref.status === "NEW" || ref.status === "SUGGESTED")) open.push(`${r.horse.era.name}: ${ref.label} ${ref.era.name}`);
      });
      if (r.entry.equipmentMissing) open.push(`${r.horse.era.name}: equipment`);
    });
    return open;
  };

  const renderFinal = () => {
    if (isTrial) return null;
    const open = blockers();
    if (!data.race.exists) {
      const problems = validateNewRace(data, raceExtras);
      const disabled = saving || open.length > 0 || problems.length > 0;
      return (
        <div className="imp-race-foot">
          <span className={disabled ? "imp-warn-text" : "imp-ok-text"}>
            {open.length ? `${open.length} still missing in Saddle Battle (see the ! marks)` : problems.length ? problems.join(" · ") : "Ready to add"}
          </span>
          <button className="imp-btn imp-btn--primary" disabled={disabled} onClick={addRace}>Add race</button>
        </div>
      );
    }
    // Added before the declarations: once its horses are on the card it can go live.
    const onCard = data.runners.filter((r) => r.entry.state !== "NEW").length;
    const publish = !data.race.published && onCard > 0 && (
      <div className="imp-race-foot">
        <span className="imp-warn-text">The race is added but not published ({onCard} of {data.runners.length} horses on the card)</span>
        <button className="imp-btn imp-btn--primary" disabled={saving} onClick={publishRace}>Publish race</button>
      </div>
    );
    const results = data.results;
    if (results.state === "NONE") return publish || <div className="imp-race-foot"><span className="imp-muted">ERA has no result for this race yet.</span></div>;
    const resultProblems = validateResult(data, resultForm);
    // Saved on every row but one value for the race: said once, above the table.
    const timeDiff = results.rows.flatMap((row) => row.diffs).find((d) => d.key === "RaceTime");
    return (
      <div className="imp-race">
        <div className="imp-race-head">
          <strong>3. Result</strong>
          {results.state === "MATCH" && <span className="imp-chip imp-chip--done"><FiCheckCircle /> Same as ERA</span>}
        </div>
        {timeDiff && (
          <div className="imp-result-note">
            <strong>Winner&apos;s time, for the whole race:</strong> System {timeDiff.db} → ERA {timeDiff.era}
          </div>
        )}
        <div className="imp-table-wrap">
        <table className="imp-table imp-table--compact">
          <thead><tr><th>Pos</th><th>Horse</th><th>Margin</th><th>Time (ERA)</th><th>SP (ERA)</th><th>Comment</th><th>Difference</th></tr></thead>
          <tbody>
            {results.rows.map((row) => (
              <tr key={row.horseId || `${row.horseNo}-${row.horseName}`} className={row.diffs.length ? "imp-row--open" : ""}>
                <td>{row.positionText}</td>
                <td>{row.horseNo}. {row.horseName}</td>
                <td>{row.marginText}</td>
                <td>{row.time}</td>
                <td>{row.startingPrice != null ? row.startingPrice : ""}</td>
                <td className="imp-small">{row.entry.Remarks}</td>
                <td className="imp-small">
                  {row.diffs.filter((d) => d.key !== "RaceTime").map((d) => (
                    d.key === "Remarks" ? (
                      <div key={d.key} className="imp-result-diff">{d.db ? "Comment is different" : "Comment missing in System"}</div>
                    ) : (
                      <div key={d.key} className="imp-result-diff">
                        <strong>{d.label}</strong> System {d.db != null && d.db !== "" ? String(d.db) : "empty"} → ERA {String(d.era)}
                      </div>
                    )
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {results.state !== "MATCH" && (
          <>
            <div className="imp-race-form">
              <label className="imp-field">
                <span className="imp-field-label">Best turned out <span className="imp-req">*</span> (not on ERA)</span>
                <select value={resultForm.bestTurnOut} onChange={(e) => setResultForm((f) => ({ ...f, bestTurnOut: e.target.value }))}>
                  <option value="">Select…</option>
                  {results.rows.filter((r) => r.horseId).map((r) => <option key={r.horseId} value={r.horseId}>{r.horseNo}. {r.horseName}</option>)}
                  {/* Saved earlier for a horse that is not in ERA's list: it stays selectable, so an update does not lose it. */}
                  {results.bestTurnOut && !results.rows.some((r) => r.horseId === results.bestTurnOut) && (
                    <option value={results.bestTurnOut}>{results.bestTurnOutName || "Current horse"}</option>
                  )}
                </select>
              </label>
              <label className="imp-field">
                <span className="imp-field-label">Best turned out prize</span>
                <input type="number" value={resultForm.bestTurnPrice} onChange={(e) => setResultForm((f) => ({ ...f, bestTurnPrice: e.target.value }))} />
              </label>
              <label className="imp-field">
                <span className="imp-field-label">Video link {results.videoLink ? "(filled from ERA)" : ""}</span>
                <input value={resultForm.videoLink} onChange={(e) => setResultForm((f) => ({ ...f, videoLink: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." />
                {results.photoFinish && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <a href={results.photoFinish} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "underline" }}>
                      📸 View Photo Finish (ERA)
                    </a>
                  </div>
                )}
              </label>
            </div>
            <div className="imp-race-foot">
              <span className="imp-warn-text">{resultProblems.join(" · ")}</span>
              <button
                className="imp-btn imp-btn--primary"
                disabled={saving || resultProblems.length > 0}
                onClick={() => setVerifying(true)}
              >
                {results.state === "DIFF" ? "Verify and update result" : "Verify and add result"}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="rightsidedata">
        <div className="imp-page">
          <div className="imp-header">
            <div>
              <h2 style={{ margin: 0 }}>Race Data Sync</h2>
              <p className="imp-muted">Load a race from Emirates Racing Authority and sync it with Saddle Battle.</p>
            </div>
            <div className="imp-load">
              {/* Not a <label>: it would pass every click in the calendar back to the input and reopen it. */}
              <div className="imp-field">
                <span className="imp-field-label">Date</span>
                <DateField value={date} onChange={setDate} raceDays={raceDays} legend={legend} />
              </div>
              <label className="imp-field">
                <span className="imp-field-label">Race number</span>
                <input type="number" min="1" style={{ width: 110 }} value={raceNo} onChange={(e) => setRaceNo(e.target.value)} />
              </label>
              <button className="imp-btn imp-btn--primary" onClick={() => load()} disabled={loading || saving}>
                <FiDownloadCloud /> {loading ? "Loading…" : "Load race"}
              </button>
            </div>
          </div>

          {loadError && <div className="imp-verdict imp-verdict--bad">{loadError}</div>}
          {!data && !loadError && !loading && <div className="imp-empty-state">Pick a date and a race number, then load it.</div>}

          {data && (
            <>
              <div className="imp-race-tabs">
                {data.source.raceNumbersOfDay.map((n) => (
                  <button
                    key={n}
                    className={`imp-race-tab ${String(n) === String(data.raceInfo.raceNo) ? "is-active" : ""}`}
                    title={TAB_DOT_TITLE[tabDot(n)]}
                    disabled={loading || saving}
                    onClick={() => {
                      setRaceNo(String(n));
                      load(n);
                    }}
                  >
                    Race {n}
                    {tabDot(n) && <span className={`imp-race-tab-dot imp-race-tab-dot--${tabDot(n)}`} />}
                  </button>
                ))}
                <a className="imp-btn imp-era-link" href={data.source.url} target="_blank" rel="noreferrer">View on ERA <FiExternalLink size={11} /></a>
              </div>
              {(date !== era.race.date || String(raceNo) !== String(era.race.raceNo)) && (
                <div className="imp-verdict imp-verdict--bad">Still showing {era.race.date}, race {era.race.raceNo}. Click Load race to open the date and race you picked.</div>
              )}
              {renderStatus()}
              {renderBanner()}
              {renderHeader()}
              {data.runners.length > 0 && renderRunners()}
              {renderFinal()}
            </>
          )}

          <Modal show={Boolean(creating)} onHide={closeCreate} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>{creating && creating.title}</Modal.Title></Modal.Header>
            <Modal.Body>
              {creating && <CreateForm key={creating.title} kind={creating.kind} draft={creating.draft} colours={data.options.colours} nationalities={data.options.nationalities} saving={saving} onCancel={closeCreate} onSave={saveCreate} />}
            </Modal.Body>
          </Modal>

          <Modal show={verifying && Boolean(data)} onHide={() => setVerifying(false)} size="xl" centered>
            <Modal.Header closeButton><Modal.Title>Verify result: Race {data && data.raceInfo.raceNo}</Modal.Title></Modal.Header>
            <Modal.Body>
              {verifying && data && (
                <>
                  <p className="imp-hint">This is exactly what will be saved in Saddle Battle. Winning time, margin and comment come from ERA.</p>
                  <table className="imp-table imp-table--compact imp-verify-table">
                    <thead><tr><th>Pos</th><th>Horse</th><th>Margin</th><th>Winner&apos;s time</th><th>Prize</th><th>Rating</th><th>Comment</th></tr></thead>
                    <tbody>
                      {data.results.rows.map((row) => (
                        <tr key={row.horseId || `${row.horseNo}-${row.horseName}`} className={row.diffs.length ? "imp-row--open" : ""}>
                          <td>{row.positionText === "NR" ? "NR (saved as UR)" : row.positionText}</td>
                          <td>{row.horseNo}. {row.horseName}</td>
                          <td>{row.entry.Distance}</td>
                          <td>{row.entry.RaceTime}</td>
                          <td>{prizeOf(row).toLocaleString()}</td>
                          <td>{row.entry.Rating}</td>
                          <td className="imp-small">{row.entry.Remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="imp-hint">
                    Best turned out: <strong>{(data.results.rows.find((r) => r.horseId === resultForm.bestTurnOut) || {}).horseName || data.results.bestTurnOutName}</strong> · prize {resultForm.bestTurnPrice} · video {resultForm.videoLink || "none"}
                  </p>
                  {!isPast && data.results.state !== "DIFF" && (
                    <p className="imp-hint imp-warn-text">People tracking these horses get an email and a notification, as when a result is added by hand.</p>
                  )}
                  <div className="imp-modal-actions">
                    <button className="imp-btn" onClick={() => setVerifying(false)}>Back</button>
                    <button className="imp-btn imp-btn--primary" disabled={saving} onClick={saveResult}>
                      {data.results.state === "DIFF" ? "Confirm and update" : "Confirm and add"}
                    </button>
                  </div>
                </>
              )}
            </Modal.Body>
          </Modal>

          <Modal show={Boolean(reviewRunner)} onHide={() => setReviewing(null)} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>Horse file: {reviewRunner && reviewRunner.horse.era.name}</Modal.Title></Modal.Header>
            <Modal.Body>
              {reviewRunner && <ReviewDiffs key={openDiffs(reviewRunner).map((d) => `${d.key}:${Boolean(d.patch)}`).join()} diffs={openDiffs(reviewRunner)} canUseEra={(d) => Boolean(d.patch)} recordActions={recordActions(reviewRunner)} eraHref={(d) => { const ref = PEDIGREE_KEY_OF[d.key] && refOfDiff(reviewRunner, d.key); return ref && ref.era ? eraHorseUrl(ref.era.eraId) : null; }} onCancel={() => setReviewing(null)} onSave={(chosen) => saveHorseFile(reviewRunner, chosen)} submitLabel="Update Horse" warningText="Add or update all horse details to Update horse" />}
            </Modal.Body>
          </Modal>

          <Modal show={Boolean(editing)} onHide={() => setEditing(null)} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>Race entry: {editing && editing.horse.era.name}</Modal.Title></Modal.Header>
            <Modal.Body>
              {editing && <ReviewDiffs diffs={openEntryDiffs(editing)} canUseEra={(d) => (editing.nonRunner ? true : d.key !== "JockeyOnRace" || Boolean(editing.jockey.dbId))} recordActions={recordActions(editing)} onCancel={() => setEditing(null)} onSave={(chosen) => saveEntry(editing, chosen)} submitLabel="Update Entry" warningText="Add or update all entry details to Update entry" />}
            </Modal.Body>
          </Modal>
        </div>
      </div>
    </div>
  );
};

/** Field by field: ERA or ours. Ours is not saved anywhere; the difference comes back on the next Load. */
const ReviewDiffs = ({ diffs, canUseEra, recordActions, eraHref, onCancel, onSave, submitLabel = "Update Horse", warningText = "Add or update all horse details to Update horse" }) => {
  const [chosen, setChosen] = useState(() => Object.fromEntries(diffs.map((d) => [d.key, canUseEra(d) ? "era" : "db"])));
  const notAdded = diffs.filter((d) => !canUseEra(d));
  const missing = notAdded.length;
  return (
    <>
      <table className="imp-table">
        <thead><tr><th>Field</th><th>System</th><th>ERA</th><th>Keep</th></tr></thead>
        <tbody>
          {diffs.map((d) => (
            <tr key={d.key}>
              <td>{d.label}</td>
              <td>{d.db != null ? String(d.db) : <span className="imp-muted">empty</span>}</td>
              <td>
                {eraHref && eraHref(d) ? (
                  <a className="imp-era-name" href={eraHref(d)} target="_blank" rel="noreferrer" title="Open on ERA"><strong>{String(d.era)}</strong> <FiExternalLink size={11} /></a>
                ) : <strong>{String(d.era)}</strong>}
                {!canUseEra(d) && <div className="imp-hint imp-warn-text">Not in Saddle Battle yet</div>}</td>
              <td>
                {/* Nothing to choose until the record exists: adding it is the only step. */}
                {canUseEra(d) ? (
                  <>
                    <label className="imp-radio">
                      <input type="radio" checked={chosen[d.key] === "era"} onChange={() => setChosen((c) => ({ ...c, [d.key]: "era" }))} /> ERA
                    </label>
                    <label className="imp-radio">
                      <input type="radio" checked={chosen[d.key] === "db"} onChange={() => setChosen((c) => ({ ...c, [d.key]: "db" }))} /> Current
                    </label>
                  </>
                ) : recordActions(d)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="imp-modal-actions">
        {missing > 0 && (
          <span className="imp-modal-note imp-warn-text" style={{ fontWeight: 600 }}>
            {missing === diffs.length
              ? warningText
              : `${notAdded.map((d) => d.label).join(", ")} not in Saddle Battle yet: ${missing > 1 ? "they stay" : "it stays"} in Review until added`}
          </span>
        )}
        <button className="imp-btn" onClick={onCancel}>Cancel</button>
        <button className="imp-btn imp-btn--danger" disabled={missing === diffs.length} onClick={() => onSave(chosen)}>
          {submitLabel}
        </button>
      </div>
    </>
  );
};

// Every field of the admin's own create forms, in their order, filled from ERA and all editable.
const YES_NO = [{ _id: 1, name: "Yes" }, { _id: 0, name: "No" }];
const FOALS = Array.from({ length: 10 }, (_, i) => ({ _id: i + 1, name: String(i + 1) }));
const LOOKUP_FIELDS = [
  ["NameEn", "Name", "text"],
  ["NameAr", "Name (Arabic)", "arabic"],
  ["shortCode", "Short code", "text"],
];
const FORM_FIELDS = {
  RaceName: LOOKUP_FIELDS,
  RaceKind: LOOKUP_FIELDS,
  RaceType: LOOKUP_FIELDS,
  horse: [
    ["KindHorse", "Horse kind", "list", "horseKinds"],
    ["shortCode", "Horse code", "text"],
    ["NameEn", "Horse name", "text"],
    ["NameAr", "Name (Arabic)", "arabic"],
    ["DOB", "Date of birth", "date"],
    ["Foal", "Foal", "fixed", FOALS],
    ["NationalityID", "Nationality", "list", "nationalities"],
    ["ColorID", "Colour", "list", "colours"],
    ["Sex", "Gender", "list", "sexes"],
    ["Dam", "Dam", "search", "horse"],
    ["Sire", "Sire", "search", "horse"],
    ["GSire", "Damsire", "search", "horse"],
    ["ActiveOwner", "Active owner", "search", "owner"],
    ["ActiveTrainer", "Active trainer", "search", "trainer"],
    ["Breeder", "Breeder", "search", "breeder"],
    ["isGelded", "Gelded", "fixed", YES_NO],
    ["Rds", "Rds", "fixed", YES_NO],
    ["HorseStatus", "Horse status (active)", "fixed", YES_NO],
    ["PurchasePrice", "Purchase price", "number"],
    ["RemarksEn", "Remarks", "text"],
    ["RemarksAr", "Remarks (Arabic)", "arabic"],
  ],
  owner: [
    ["NameEn", "Name", "text"],
    ["NameAr", "Name (Arabic)", "arabic"],
    ["TitleEn", "Title", "text"],
    ["TitleAr", "Title (Arabic)", "arabic"],
    ["ShortEn", "Short name", "text"],
    ["ShortAr", "Short name (Arabic)", "arabic"],
    ["shortCode", "Short code", "text"],
    ["RegistrationDate", "Registration date", "date"],
    ["NationalityID", "Nationality", "list", "nationalities"],
  ],
  trainer: [
    ["NameEn", "Name", "text"],
    ["NameAr", "Name (Arabic)", "arabic"],
    ["DOB", "Date of birth", "date"],
    ["TrainerLicenseDate", "Trainer licence date", "date"],
    ["TitleEn", "Title", "text"],
    ["TitleAr", "Title (Arabic)", "arabic"],
    ["ShortNameEn", "Short name", "text"],
    ["ShortNameAr", "Short name (Arabic)", "arabic"],
    ["shortCode", "Short code", "text"],
    ["DetailEn", "Detail", "text"],
    ["DetailAr", "Detail (Arabic)", "arabic"],
    ["NationalityID", "Nationality", "list", "nationalities"],
    ["RemarksEn", "Remarks", "text"],
    ["RemarksAr", "Remarks (Arabic)", "arabic"],
  ],
  jockey: [
    ["NameEn", "Name", "text"],
    ["NameAr", "Name (Arabic)", "arabic"],
    ["ShortNameEn", "Short name", "text"],
    ["ShortNameAr", "Short name (Arabic)", "arabic"],
    ["shortCode", "Short code", "text"],
    ["RemarksEn", "Remarks", "text"],
    ["RemarksAr", "Remarks (Arabic)", "arabic"],
    ["DOB", "Date of birth", "date"],
    ["JockeyLicenseDate", "Jockey licence date", "date"],
    ["NationalityID", "Nationality", "list", "nationalities"],
    ["Rating", "Rating", "number"],
    ["MiniumumJockeyWeight", "Jockey minimum weight", "number"],
    ["MaximumJockeyWeight", "Jockey maximum weight", "number"],
    ["JockeyAllowance", "Jockey allowance", "number"],
  ],
};

// Sex and breed: short lists, asked once per page.
let shortLists = null;
const loadShortLists = () => {
  if (!shortLists) {
    shortLists = axios.get(api("/aiscraper/form-lists")).then((res) => res.data.data).catch(() => {
      shortLists = null; // asked again next time
      return { sexes: [], horseKinds: [] };
    });
  }
  return shortLists;
};

/** Owner, trainer, breeder or horse: typed to search, as the admin form's dropdown; shows the one already chosen. */
const SearchField = ({ type, value, onChange }) => {
  const [text, setText] = useState("");
  const [chosen, setChosen] = useState(null);
  const [found, setFound] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!value) return setChosen(null);
    if (chosen && chosen._id === value) return undefined;
    let live = true;
    axios.get(api("/aiscraper/search"), { params: { type, ids: value } }).then((res) => live && setChosen(res.data.data[0] || { _id: value, name: "(not found)" })).catch(() => {});
    return () => {
      live = false;
    };
  }, [type, value]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (text.trim().length < 2) return setFound([]);
    let live = true;
    const wait = setTimeout(() => {
      axios.get(api("/aiscraper/search"), { params: { type, q: text.trim() } }).then((res) => live && setFound(res.data.data)).catch(() => {});
    }, 300);
    return () => {
      live = false;
      clearTimeout(wait);
    };
  }, [type, text]);
  // A click anywhere outside closes the list and puts the chosen name back, focus or no focus.
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const chosenLabel = chosen ? `${chosen.name}${chosen.shortCode ? ` #${chosen.shortCode}` : ""}` : "";
  return (
    <div className="imp-pick" ref={box}>
      <input
        // The chosen name stays on a click, selected, so typing replaces it; leaving without a pick keeps it.
        value={open ? text : chosenLabel}
        placeholder="Type 2 letters to search…"
        onFocus={(e) => {
          const box = e.target;
          setText("");
          setOpen(false);
          setTimeout(() => box.select(), 0);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
      />
      {open && found.length > 0 && (
        <ul className="imp-pick-list">
          {found.map((row) => (
            <li key={row._id}>
              <button
                type="button"
                onMouseDown={() => {
                  setChosen(row);
                  onChange(row._id);
                  setOpen(false);
                }}
              >
                {row.name}{row.shortCode ? ` #${row.shortCode}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Pre-filled create form: the admin form's fields, filled from ERA. */
const CreateForm = ({ kind, draft, colours, nationalities, saving, onCancel, onSave }) => {
  const [values, setValues] = useState(draft);
  const [lists, setLists] = useState({ sexes: [], horseKinds: [] });
  useEffect(() => {
    if (kind !== "horse") return undefined;
    let live = true;
    loadShortLists().then((got) => live && setLists(got));
    return () => {
      live = false;
    };
  }, [kind]);
  // An Arabian horse or owner: its Arabic name from the UAE studbook (EAHS), asked once the form is open so it never waits for it.
  const [arabic, setArabic] = useState(["horse", "owner"].includes(kind) ? "looking" : null);
  useEffect(() => {
    if (!["horse", "owner"].includes(kind) || !draft.NameEn) return undefined;
    let open = true;
    axios
      .get(api("/aiscraper/arabic"), {
        params: {
          kind,
          name: draft.NameEn,
          year: kind === "horse" ? String(draft.DOB || "").slice(0, 4) || undefined : undefined,
        },
      })
      .then((res) => {
        if (!open) return;
        const found = res.data.data;
        setArabic(found || null);
        // Only over the English placeholder: an Arabic name the admin already typed stays.
        if (found) {
          setValues((v) => {
            const next = { ...v };
            if (v.NameAr === draft.NameAr) next.NameAr = found.nameAr;
            if (kind === "owner") {
              if (v.TitleAr === draft.TitleAr) next.TitleAr = found.nameAr;
              if (v.ShortAr === draft.ShortAr) next.ShortAr = found.nameAr;
            }
            return next;
          });
        }
      })
      .catch(() => open && setArabic(null));
    return () => {
      open = false;
    };
  }, [kind, draft.NameEn, draft.DOB, draft.NameAr, draft.TitleAr, draft.ShortAr]);

  const set = (key, value) => setValues((v) => ({ ...v, [key]: value, ...(key === "NationalityID" ? { CreationId: value } : {}) }));
  const optionsOf = (source) => (Array.isArray(source) ? source : { colours, nationalities, ...lists }[source] || []);
  const required = PERSON_REQUIRED[kind];
  const problems = validateRecord(kind, values, required, MISSING_LABEL);
  const fields = FORM_FIELDS[kind].filter(([key]) => key in draft || ["isGelded", "PurchasePrice"].includes(key));

  const input = ([key, , type, source]) => {
    const value = values[key] != null ? values[key] : "";
    if (type === "date") return <DateField value={values[key]} disableFuture onChange={(day) => set(key, day)} />;
    if (type === "search") return <SearchField type={source} value={values[key]} onChange={(id) => set(key, id)} />;
    if (type === "list" || type === "fixed") {
      return (
        <select value={value} onChange={(e) => set(key, type === "fixed" ? Number(e.target.value) : e.target.value)}>
          <option value="">Select…</option>
          {optionsOf(source).map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
        </select>
      );
    }
    return (
      <input
        type={type === "number" ? "number" : "text"}
        dir={type === "arabic" ? "rtl" : undefined}
        value={value}
        onChange={(e) => set(key, e.target.value)}
      />
    );
  };

  return (
    <>
      <div className="imp-form-grid">
        {fields.map((field) => {
          const [key, label, type] = field;
          const FieldTag = type === "date" || type === "search" ? "div" : "label";
          return (
            <FieldTag key={key} className="imp-field">
              <span className="imp-field-label">{label}{required.includes(key) && <span className="imp-req"> *</span>}</span>
              {input(field)}
              {key === "NameAr" && kind === "horse" && (
                <span className="imp-hint">
                  {arabic === "looking"
                    ? "Looking for the Arabic name on EAHS…"
                    : arabic
                      ? `Arabic from EAHS (reg. ${arabic.regNo})`
                      : "Not on EAHS (Arabian horses only): English for now, correct the Arabic later"}
                </span>
              )}
              {key === "NameAr" && kind === "owner" && (
                <span className="imp-hint">
                  {arabic === "looking"
                    ? "Looking for owner Arabic name on EAHS…"
                    : arabic
                      ? `Arabic from EAHS (reg. ${arabic.regNo})`
                      : "Not on EAHS: English for now, correct the Arabic later"}
                </span>
              )}
              {key === "NameAr" && LOOKUP_PATH[kind] && (
                <span className="imp-hint">{draft.arabicFromList ? "Arabic from the list" : "Not in the list: English for now, correct the Arabic"}</span>
              )}
              {key === "DOB" && kind !== "horse" && !values.DOB && <span className="imp-hint">Not on ERA: please enter it</span>}
              {key === "Breeder" && draft.breederOnEra && <span className="imp-hint">On ERA: {draft.breederOnEra}</span>}
              {key === "NationalityID" && draft.countryOnEra && !values.NationalityID && (
                <span className="imp-hint">ERA says {draft.countryOnEra}: not in the Nationality list. Choose it, or add the country first.</span>
              )}
            </FieldTag>
          );
        })}
      </div>
      {kind === "owner" && draft.silkUrl && (
        <label className="imp-silk-option">
          <input type="checkbox" checked={values.useSilk !== false} onChange={(e) => {
            const on = e.target.checked;
            setValues((v) => ({ ...v, useSilk: on }));
          }} />
          <img className="imp-silk-img" src={draft.silkUrl} alt="ERA silk" />
          <span>Copy this silk from ERA</span>
        </label>
      )}
      <div className="imp-modal-actions">
        {problems.length > 0 && <span className="imp-hint imp-warn-text imp-grow">{problems.join(" · ")}</span>}
        <button className="imp-btn" onClick={onCancel}>Cancel</button>
        <button
          className="imp-btn imp-btn--primary"
          disabled={saving || problems.length > 0}
          onClick={() => {
            const { useSilk, ...body } = values;
            onSave(useSilk === false ? { ...body, silkUrl: null } : body);
          }}
        >
          Add to Saddle Battle
        </button>
      </div>
    </>
  );
};

export default AiScraper;
